import { Alert, Button, Form, Input } from 'antd';
import { Controller, useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { useChangePasswordMutation, type ChangePasswordRequest } from '@/entities/user';
import { translateApiError } from '@/shared/api/errorToMessage';
import { ROUTES } from '@/shared/config/routes';
import { useBreakpoint } from '@/shared/lib/hooks';
import { touchPasswordIcon } from '@/shared/lib/mobileUi';

/** Телефон: поле 44px, без автозаглавной/автокоррекции, значок «показать пароль» — цель касания 44px. */
const MOBILE_CURRENT_PASSWORD_INPUT = { size: 'large', autoCapitalize: 'none', autoCorrect: 'off', spellCheck: false, enterKeyHint: 'next', iconRender: touchPasswordIcon } as const;
const MOBILE_NEW_PASSWORD_INPUT = { size: 'large', autoCapitalize: 'none', autoCorrect: 'off', spellCheck: false, enterKeyHint: 'go', iconRender: touchPasswordIcon } as const;

export function ChangePasswordForm() {
  const navigate = useNavigate();
  const { isMobile } = useBreakpoint();
  const [changePassword, { isLoading, error }] = useChangePasswordMutation();
  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<ChangePasswordRequest>({ defaultValues: { currentPassword: '', newPassword: '' } });

  const onSubmit = handleSubmit((values) => {
    changePassword(values)
      .unwrap()
      .then(() => void navigate(ROUTES.login, { replace: true }))
      .catch(() => undefined);
  });

  return (
    <Form layout="vertical" onFinish={() => void onSubmit()}>
      <Form.Item
        label="Текущий пароль"
        validateStatus={errors.currentPassword ? 'error' : ''}
        help={errors.currentPassword ? 'Обязательное поле' : undefined}
      >
        <Controller
          name="currentPassword"
          control={control}
          rules={{ required: true }}
          render={({ field }) => (
            // На телефоне автофокус не ставим: он сразу поднимает экранную клавиатуру.
            <Input.Password
              {...field}
              {...(isMobile ? MOBILE_CURRENT_PASSWORD_INPUT : {})}
              autoComplete="current-password"
              autoFocus={!isMobile}
            />
          )}
        />
      </Form.Item>
      <Form.Item
        label="Новый пароль"
        validateStatus={errors.newPassword ? 'error' : ''}
        help={errors.newPassword ? 'Пароль должен содержать минимум 8 символов' : undefined}
      >
        <Controller
          name="newPassword"
          control={control}
          rules={{ required: true, minLength: 8 }}
          render={({ field }) => (
            <Input.Password {...field} {...(isMobile ? MOBILE_NEW_PASSWORD_INPUT : {})} autoComplete="new-password" />
          )}
        />
      </Form.Item>
      {error ? (
        <Alert
          type="error"
          showIcon
          message={translateApiError(error)}
          style={{ marginBottom: 16 }}
        />
      ) : null}
      <Button type="primary" htmlType="submit" block loading={isLoading} size={isMobile ? 'large' : undefined}>
        Сменить пароль
      </Button>
    </Form>
  );
}
